import React from "react";

import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface FontSizeSelectorProps {
  fontSize: number | null;
  onSelect: (fontSize: number) => void;
  placeholder: string;
}

const generateFontSizeOptions = () => {
  const sizes = [10, 12, 14, 16, 18, 20, 24, 30, 36, 48, 60, 72];
  return sizes.map((size) => ({
    value: size.toString(),
    label: `${size}px`,
    displayValue: `${size}px`,
  }));
};

const FONT_SIZE_OPTIONS = generateFontSizeOptions();

const FontSizeSelector: React.FC<FontSizeSelectorProps> = ({
  fontSize,
  onSelect,
  placeholder,
}) => {
  const [selectedFontSize, setSelectedFontSize] = useOverridableState(
    fontSize?.toString() ?? null,
    (value) => onSelect(value ? Number(value) : 0),
  );

  const currentOption = FONT_SIZE_OPTIONS.find(
    (option) => option.value === selectedFontSize,
  );

  const fontSizeOptions = FONT_SIZE_OPTIONS.map((size) => ({
    ...size,
    label: (
      <MenuItem
        variant="default"
        size="sm"
        selected={selectedFontSize === size.value}
      >
        {size.label}
      </MenuItem>
    ),
  }));

  return (
    <div className="w-full overflow-hidden">
      <Combobox.Root
        options={fontSizeOptions}
        value={selectedFontSize ?? undefined}
        onChange={(value) => setSelectedFontSize(value)}
      >
        <Combobox.Trigger>
          <Combobox.SelectionButton
            layoutClassName="w-full"
            title={
              currentOption?.displayValue ??
              (selectedFontSize ? `${selectedFontSize}px` : placeholder)
            }
            isPlaceholder={!selectedFontSize}
            size="sm"
            endEnhancer={<ChevronDown size={12} className="text-muted" />}
            titleAlignment="start"
          />
        </Combobox.Trigger>
        <Combobox.Popover align="center" layoutClassName="w-[140px]">
          <Combobox.Content
            areOptionsSearchable
            emptySearchMessage="No matches found"
          />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default FontSizeSelector;
